import { useState } from "react";
import { ListSubheader, SelectChangeEvent } from "@mui/material";
import DSStack from "views/system-design/DSStack";
import DSTextField from "views/system-design/DSTextField";
import DSSelect from "views/system-design/DSSelect";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSButton from "views/system-design/DSButton";

/** An all-time range is both bounds absent. */
export type PaymentsRange = { from?: string; to?: string };

const ALL_TIME = "all";
const CUSTOM = "custom";
const THIS_MONTH = "this-month";
const PREVIOUS_MONTH = "previous-month";

// Rolling windows, offered alongside the calendar months.
const PRESET_DAYS = [30, 90, 365] as const;

const toDateInput = (date: Date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const daysAgo = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateInput(date);
};

const toUtcDateInput = (date: Date) => {
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${date.getUTCFullYear()}-${month}-${day}`;
};

/**
 * First and last day of one calendar month. Computed in UTC to line up with the
 * entitlement months the API reports — a local-time boundary would pull a
 * payment made near midnight into the neighbouring month.
 */
const monthBounds = (year: number, monthIndex: number) => ({
  from: toUtcDateInput(new Date(Date.UTC(year, monthIndex, 1))),
  // Day 0 of the next month is the last day of this one.
  to: toUtcDateInput(new Date(Date.UTC(year, monthIndex + 1, 0))),
});

/** Whole calendar month, `monthsAgo` back from now. */
const calendarMonthRange = (monthsAgo: number) => {
  const now = new Date();
  return monthBounds(now.getUTCFullYear(), now.getUTCMonth() - monthsAgo);
};

/** Whole calendar month containing the given 'YYYY-MM-DD' date. */
const calendarMonthRangeFor = (isoDate: string) => {
  const [year, month] = isoDate.split("-").map(Number);
  return monthBounds(year, month - 1);
};

/** Names the calendar month `monthsAgo` back, e.g. 'Aug 2026'. */
const monthLabel = (monthsAgo: number) => {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - monthsAgo, 1),
  ).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

/**
 * Names a range for an export filename. A range covering exactly one whole
 * calendar month is named by that month rather than by both endpoints.
 */
export const rangeLabel = ({ from, to }: PaymentsRange) => {
  if (!from || !to) return "all-time";
  const wholeMonth = calendarMonthRangeFor(from);
  if (wholeMonth.from === from && wholeMonth.to === to) {
    return from.slice(0, 7);
  }
  return `${from}_${to}`;
};

/**
 * Range picker shared by the artist payment widgets: all time, whole calendar
 * months, rolling day windows, or a custom pair of dates.
 */
function PaymentsRangeFilter({
  onChange,
  initialRange,
}: {
  onChange: (range: PaymentsRange) => void;
  /** Where the filter opens, e.g. a period taken from a shared link. Read once. */
  initialRange?: PaymentsRange;
}) {
  // Two dates open as a custom range with the dates filled in; anything else is all time.
  const startsCustom = !!initialRange?.from && !!initialRange?.to;
  const [preset, setPreset] = useState<string>(
    startsCustom ? CUSTOM : ALL_TIME,
  );
  const [from, setFrom] = useState(startsCustom ? initialRange!.from! : "");
  const [to, setTo] = useState(startsCustom ? initialRange!.to! : "");

  const handlePresetChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;
    setPreset(value);
    if (value === ALL_TIME) {
      onChange({});
    } else if (value === THIS_MONTH) {
      onChange(calendarMonthRange(0));
    } else if (value === PREVIOUS_MONTH) {
      onChange(calendarMonthRange(1));
    } else if (value !== CUSTOM) {
      onChange({ from: daysAgo(Number(value)), to: toDateInput(new Date()) });
    }
  };

  const canApply = !!from && !!to && from <= to;

  return (
    <>
      <DSSelect
        size="small"
        value={preset}
        onChange={handlePresetChange}
        sx={{ minWidth: 140 }}
      >
        <DSMenuItem value={ALL_TIME}>All time</DSMenuItem>
        <ListSubheader>Calendar month</ListSubheader>
        <DSMenuItem value={THIS_MONTH}>This month ({monthLabel(0)})</DSMenuItem>
        <DSMenuItem value={PREVIOUS_MONTH}>
          Previous month ({monthLabel(1)})
        </DSMenuItem>
        <ListSubheader>Rolling window</ListSubheader>
        {PRESET_DAYS.map((days) => (
          <DSMenuItem key={days} value={String(days)}>
            Last {days} days
          </DSMenuItem>
        ))}
        <DSMenuItem value={CUSTOM}>Custom range</DSMenuItem>
      </DSSelect>
      {preset === CUSTOM && (
        <DSStack direction="row" alignItems="center" spacing={1}>
          <DSTextField
            type="date"
            size="small"
            label="From"
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: to || toDateInput(new Date()) }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            sx={{ width: 150 }}
          />
          <DSTextField
            type="date"
            size="small"
            label="To"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: from, max: toDateInput(new Date()) }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            sx={{ width: 150 }}
          />
          <DSButton
            size="small"
            variant="contained"
            disabled={!canApply}
            onClick={() => onChange({ from, to })}
          >
            Apply
          </DSButton>
        </DSStack>
      )}
    </>
  );
}

export default PaymentsRangeFilter;
